/**
 * The metadata strip (decision 37; part 16a log, table S): an encoded JPEG or PNG cut down to what draws it. Every
 * segment or chunk that can carry metadata (EXIF and its GPS position, XMP, ICC profiles, comments, text chunks) is
 * dropped, whatever the encoder wrote; anything this module doesn't know refuses the file rather than pass through.
 */
import { byteAt, EOI, fourCc, holds, isFrame, segmentAt, SOI, SOS } from './header.ts';
import type { ImageFormat } from './header.ts';

/** S1: the JPEG markers kept besides frames, scans and EOI: DQT, DHT, DAC, DRI. */
const JPEG_TABLES = new Set([0xdb, 0xc4, 0xcc, 0xdd]);

/** S2: whether a JPEG marker is APP0–APP15 or COM, which are dropped. */
function isMetadata(marker: number): boolean {
  return (marker >= 0xe0 && marker <= 0xef) || marker === 0xfe;
}

/**
 * Where the entropy-coded data starting at `from` ends: at the next marker that isn't a stuffed 0xFF00 or a restart
 * (RST0–RST7), or at the end of `bytes`, where no segment can be read (S4).
 */
function scanEnd(bytes: Uint8Array, from: number): number {
  for (let at = from; at + 1 < bytes.length; at++) {
    if (bytes[at] !== 0xff) continue;
    const next = byteAt(bytes, at + 1);
    if (next !== 0x00 && next !== 0xff && (next < 0xd0 || next > 0xd7)) return at;
  }
  return bytes.length;
}

/** The ranges of `bytes` laid end to end. */
function join(bytes: Uint8Array, ranges: readonly (readonly [number, number])[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(ranges.reduce((total, [from, to]) => total + to - from, 0));
  let at = 0;
  for (const [from, to] of ranges) {
    out.set(bytes.subarray(from, to), at);
    at += to - from;
  }
  return out;
}

/** S1–S4: a JPEG with only SOI, its tables, frames and scans, and EOI; null for anything else. */
function stripJpeg(bytes: Uint8Array): Uint8Array<ArrayBuffer> | null {
  if (!holds(bytes, [0xff, SOI])) return null;
  const kept: (readonly [number, number])[] = [[0, 2]];
  let frame = false;
  let scan = false;
  let at = 2;
  for (;;) {
    const segment = segmentAt(bytes, at);
    if (segment === null) return null;
    const { marker } = segment;
    if (marker === EOI) {
      kept.push([segment.start, segment.end]);
      // S3: whatever follows the end is dropped.
      return frame && scan ? join(bytes, kept) : null;
    }
    at = segment.end;
    if (isMetadata(marker)) continue;
    if (!isFrame(marker) && !JPEG_TABLES.has(marker) && marker !== SOS) return null;
    kept.push([segment.start, segment.end]);
    frame ||= isFrame(marker);
    if (marker !== SOS) continue;
    if (!frame) return null;
    const end = scanEnd(bytes, at);
    kept.push([at, end]);
    scan = true;
    at = end;
  }
}

const PNG_HEAD = 8;
/** S5: the critical chunks kept (besides IEND, which ends the file). */
const PNG_CRITICAL = new Set(['IHDR', 'PLTE', 'IDAT']);

/** S5–S8: a PNG with only IHDR first, PLTE, tRNS, IDAT and IEND; null for anything else. */
function stripPng(bytes: Uint8Array): Uint8Array<ArrayBuffer> | null {
  const kept: (readonly [number, number])[] = [[0, PNG_HEAD]];
  let data = false;
  let at = PNG_HEAD;
  for (;;) {
    // A chunk is its length, type, data and CRC.
    if (at + 12 > bytes.length) return null;
    const end = at + 12 + new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(at);
    if (end > bytes.length) return null;
    const type = fourCc(bytes, at + 4);
    if ((type === 'IHDR') !== (at === PNG_HEAD)) return null;
    if (type === 'IEND') {
      kept.push([at, end]);
      // S7: whatever follows the end is dropped.
      return data ? join(bytes, kept) : null;
    }
    // A chunk is critical when its first letter is upper case (bit 5 clear).
    const critical = (byteAt(bytes, at + 4) & 0x20) === 0;
    if (critical && !PNG_CRITICAL.has(type)) return null;
    if (critical || type === 'tRNS') kept.push([at, end]);
    data ||= type === 'IDAT';
    at = end;
  }
}

/**
 * Table S: `bytes`, an encoded `format` file, with every segment or chunk that isn't part of the picture dropped; null
 * when it isn't a well-formed file of that format or holds a part this module doesn't know.
 */
export function stripImage(bytes: Uint8Array, format: ImageFormat): Uint8Array<ArrayBuffer> | null {
  if (format === 'jpeg') return stripJpeg(bytes);
  return holds(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) ? stripPng(bytes) : null;
}

/**
 * Whether `bytes` is a `format` file the strip leaves as it is: nothing in it but the picture (principle 1). The strip's
 * output is ranges of its input, in order, from its first byte; so it is as long as the input only when it is the input.
 */
export function isStripped(bytes: Uint8Array, format: ImageFormat): boolean {
  return stripImage(bytes, format)?.length === bytes.length;
}
